// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { RECURRING_ORDER_STATUS } from '@seedplanner/shared-utils';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import {
  DateRangeIcon,
  DeleteForeverIcon,
  EditIcon,
  FileCopyIcon,
  PauseCircleOutlineIcon,
  PlayCircleOutlineIcon,
} from '../../lib/icons';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { RECURRING_ORDER_STATUS_MUTATION } from '../../queries/RecurringOrderStatusChange';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import * as routes from '../../routes';
import type {
  EditRecurringOrderStatusMutation,
  EditRecurringOrderStatusMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, getTimeRanges } from '../../utils';
import { Options } from '../common/dropdown';
import AddOrderDialog from '../order/AddOrderDialog';
import CreateOverdueOrdersDialog from './CreateOverdueOrdersDialog';
import DeleteOrPauseRecurringOrderDialog from './DeleteOrPauseRecurringOrderDialog';
import EditRecurringOrderCustomerDialog from './EditRecurringOrderCustomerDialog';
import EditRecurringOrderInputsDialog from './EditRecurringOrderInputsDialog';
import EditRecurringOrderNotesDialog from './EditRecurringOrderNotesDialog';
import EditRecurringOrderTimesDialog from './EditRecurringOrderTimesDialog';

interface Props {
  readonly recurringOrderId: number;
  readonly onSuccess: (shouldRefetchSelf: boolean, navigateTo?: string) => void;
  readonly useMoreIcon?: boolean;
  readonly status: string;
}

export default React.memo(function RecurringOrderOptions({ recurringOrderId, status, onSuccess, useMoreIcon }: Props) {
  const { canUseBasic } = useGating();
  const { timezone } = useUserContext();
  const navigate = useNavigate();
  const [cloneOpen, setCloneOpen] = React.useState(false);
  const [editCustomerOpen, setEditCustomerOpen] = React.useState(false);
  const [editNotesOpen, setEditNotesOpen] = React.useState(false);
  const [editTimesOpen, setEditTimesOpen] = React.useState(false);
  const [editInputsOpen, setEditInputsOpen] = React.useState(false);
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteDialogForDelete, setDeleteDialogForDelete] = React.useState(false);
  const [createOverdueOrdersOpen, setCreateOverdueOrdersOpen] = React.useState(false);
  const [updateRecurringOrderStatus, { loading: updateRecurringOrderStatusLoading }] = useMutation<
    EditRecurringOrderStatusMutation,
    EditRecurringOrderStatusMutationVariables
  >(RECURRING_ORDER_STATUS_MUTATION);

  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const editCustomerItem = (
    <MenuItem
      key="edit-recurring-order-customer"
      onClick={() => {
        setEditCustomerOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Customer
    </MenuItem>
  );
  const editNotesItem = (
    <MenuItem
      key="edit-recurring-order-notes"
      onClick={() => {
        setEditNotesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );
  const editTimesItem = (
    <MenuItem
      key="edit-recurring-order-times"
      onClick={() => {
        setEditTimesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Times
    </MenuItem>
  );
  const editInputsItem = (
    <MenuItem
      key="edit-recurring-order-inputs"
      onClick={() => {
        setEditInputsOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Contents
    </MenuItem>
  );
  const clone = (
    <MenuItem
      key="clone-recurring-order"
      onClick={() => {
        setCloneOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <FileCopyIcon />
      Clone
    </MenuItem>
  );
  const pause = (
    <MenuItem
      key="pause-recurring-order"
      onClick={() => {
        setDeleteDialogOpen(true);
        setDeleteDialogForDelete(false);
        setOptionsClose(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <PauseCircleOutlineIcon />
      Pause
    </MenuItem>
  );
  const unpause = (
    <MenuItem
      key="unpause-recurring-order"
      disabled={updateRecurringOrderStatusLoading || !canUseBasic}
      onClick={() => {
        void updateRecurringOrderStatus({
          refetchQueries: [
            {
              query: DASHBOARD_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
              },
            },
            {
              query: DASHBOARD_OVERDUE_TASKS_QUERY,
              variables: {
                todayStart: todayStart.toString(),
                first: 25,
              },
            },
            {
              query: SURPLUSES_BUFFERS_QUERY,
              variables: {
                timeAfter: todayStart,
              },
            },
          ],
          update: (cache, { data }) => {
            const plantingsCreated = (data?.editRecurringOrderStatus?.plantings_created?.length ?? 0) > 0;
            const tasksCreated = (data?.editRecurringOrderStatus?.tasks_created?.length ?? 0) > 0;
            const ordersCreated = (data?.editRecurringOrderStatus?.orders_created?.length ?? 0) > 0;
            const ordersDeleted = (data?.editRecurringOrderStatus?.deleted_orders?.length ?? 0) > 0;
            cache.modify({
              fields: {
                harvests(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantings(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                task_groups(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                tasks(existingTasks, { DELETE }) {
                  return tasksCreated ? DELETE : existingTasks;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantingTimeGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                orders(existingOrders, { DELETE }) {
                  return ordersCreated || ordersDeleted ? DELETE : existingOrders;
                },
              },
            });
          },
          variables: { id: recurringOrderId, new_status: RECURRING_ORDER_STATUS.ACTIVE },
          onCompleted: () => {
            enqueueSnackbar('Unpaused recurring order', { variant: 'success' });
            onSuccess(true);
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
        setOptionsClose(true);
      }}
      disableRipple
    >
      <PlayCircleOutlineIcon />
      Unpause
    </MenuItem>
  );
  const deleteItem = (
    <MenuItem
      key="delete-recurring-order"
      onClick={() => {
        setDeleteDialogOpen(true);
        setDeleteDialogForDelete(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <DeleteForeverIcon />
      Delete
    </MenuItem>
  );
  const createOverdueOrdersItem = (
    <MenuItem
      key="create-overdue-orders"
      onClick={() => {
        setCreateOverdueOrdersOpen(true);
        setOptionsClose(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <DateRangeIcon />
      Create Overdue Orders
    </MenuItem>
  );

  return (
    <>
      <Options
        useMoreIcon={useMoreIcon}
        close={optionsClose}
        options={[
          editInputsItem,
          editTimesItem,
          editCustomerItem,
          editNotesItem,
          createOverdueOrdersItem,
          status === RECURRING_ORDER_STATUS.ACTIVE ? pause : unpause,
          clone,
          deleteItem,
        ]}
      />
      {deleteDialogOpen && (
        <DeleteOrPauseRecurringOrderDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={createID.recurringOrder(recurringOrderId)}
          isForDelete={deleteDialogForDelete}
          onSuccess={() => {
            setDeleteDialogOpen(false);
            setOptionsClose(true);
            if (deleteDialogForDelete) {
              navigate(routes.RECURRING_ORDERS);
            }
          }}
        />
      )}
      {cloneOpen && (
        <AddOrderDialog
          isOpen={cloneOpen}
          onClose={() => {
            setCloneOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setCloneOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
          isForNewSingleOrder={false}
        />
      )}
      {editCustomerOpen && (
        <EditRecurringOrderCustomerDialog
          isOpen={editCustomerOpen}
          onClose={() => {
            setEditCustomerOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setEditCustomerOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
        />
      )}
      {editInputsOpen && (
        <EditRecurringOrderInputsDialog
          isOpen={editInputsOpen}
          onClose={() => {
            setEditInputsOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setEditInputsOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
        />
      )}
      {editNotesOpen && (
        <EditRecurringOrderNotesDialog
          isOpen={editNotesOpen}
          onClose={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
            onSuccess(false);
          }}
        />
      )}
      {editTimesOpen && (
        <EditRecurringOrderTimesDialog
          isOpen={editTimesOpen}
          onClose={() => {
            setEditTimesOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setEditTimesOpen(false);
            setOptionsClose(true);
            onSuccess(true);
          }}
        />
      )}
      {createOverdueOrdersOpen && (
        <CreateOverdueOrdersDialog
          isOpen={createOverdueOrdersOpen}
          onClose={() => {
            setCreateOverdueOrdersOpen(false);
            setOptionsClose(true);
          }}
          recurringOrderId={recurringOrderId}
          onSuccess={() => {
            setCreateOverdueOrdersOpen(false);
            setOptionsClose(true);
            onSuccess(true);
          }}
        />
      )}
    </>
  );
});
